import { useEffect, type RefObject } from 'react';
import { useBoardStore } from './boardStore';
import { revealTile } from './revealTile';
import type { Sized } from './masonry';

/**
 * The board's side of the masonry store: feed it what to lay out, and get
 * back where everything goes.
 *
 * Split out of `Board` because none of it is about this screen in particular —
 * it's the same five effects whatever the board is showing, and `Board` reads
 * as a screen again without them.
 */
export function useBoardLayout({
  roomId,
  sectionId,
  sized,
  width,
  itemIds,
  openItemId,
  grid,
}: {
  roomId: string | undefined;
  sectionId: string | undefined;
  sized: readonly Sized[];
  width: number;
  /** Items in board order — what the panel's arrow keys walk. */
  itemIds: readonly string[];
  openItemId: string | undefined;
  grid: RefObject<HTMLElement | null>;
}) {
  const measure = useBoardStore((state) => state.measure);
  const setSequence = useBoardStore((state) => state.setSequence);
  const markLaidOut = useBoardStore((state) => state.markLaidOut);
  const clear = useBoardStore((state) => state.clear);

  const placements = useBoardStore((state) => state.byId);
  const height = useBoardStore((state) => state.height);
  const laidOut = useBoardStore((state) => state.laidOut);

  useEffect(() => {
    if (roomId === undefined || width <= 0) return;
    measure(`${roomId}/${sectionId ?? ''}`, sized, width);
  }, [roomId, sectionId, sized, width, measure]);

  useEffect(() => {
    setSequence([...itemIds]);
  }, [itemIds, setSequence]);

  // Don't animate on first paint: tiles appear in position, and only start
  // gliding once they've been somewhere. One frame is enough for the first
  // pass to have painted.
  useEffect(() => {
    if (laidOut || placements.size === 0) return;
    const frame = requestAnimationFrame(markLaidOut);
    return () => cancelAnimationFrame(frame);
  }, [laidOut, placements, markLaidOut]);

  useEffect(() => clear, [clear]);

  // After the layout that made room for the panel, not before it.
  useEffect(() => {
    if (openItemId === undefined) return;

    const placement = placements.get(openItemId);
    const node = grid.current;
    if (placement === undefined || node === null) return;

    revealTile(node, placement);
  }, [openItemId, placements, grid]);

  return { placements, height, laidOut };
}
